import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GitMerge, Image as ImageIcon, Info, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { api, fileUrl, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

export default function Parents() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(null); // parent doc
  const [renameName, setRenameName] = useState("");
  const [renameNote, setRenameNote] = useState("");
  const [renamePhoto, setRenamePhoto] = useState(null); // storage path or "" clear
  const [renameConfirm, setRenameConfirm] = useState(null);
  const [merging, setMerging] = useState(null); // parent
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeConfirm, setMergeConfirm] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    const { data } = await api.get(`/admin/parents?${params.toString()}`);
    setItems(data);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items, [items]);

  const openRename = (p) => {
    setRenaming(p);
    setRenameName(p.name);
    setRenameNote(p.note || "");
    setRenamePhoto(p.photo_storage_path ?? null);
  };
  const submitRename = async () => {
    if (!renaming) return;
    const next = (renameName || "").trim();
    const noteChanged = (renameNote || "") !== (renaming.note || "");
    const photoChanged = (renamePhoto || null) !== (renaming.photo_storage_path || null);
    if (!next) { setRenaming(null); return; }
    // If only note/photo changed (name same), apply directly without cascade prompt.
    if (next === renaming.name) {
      if (!noteChanged && !photoChanged) { setRenaming(null); return; }
      try {
        const payload = {};
        if (noteChanged) payload.note = renameNote;
        if (photoChanged) payload.photo_storage_path = renamePhoto || "";
        await api.patch(`/admin/parents/${renaming.id}`, payload);
        toast.success("Parent updated");
        setRenaming(null); load();
      } catch (err) { toast.error(getErrorMessage(err, "Save failed")); }
      return;
    }
    // Name changed → confirm cascade count.
    const total = (renaming.usage_free_text || 0) + (renaming.usage_linked || 0);
    setRenameConfirm({ parent: renaming, newName: next, newNote: renameNote, newPhoto: renamePhoto, affected: total });
  };
  const applyRename = async () => {
    const { parent, newName, newNote, newPhoto } = renameConfirm;
    try {
      const payload = { name: newName };
      if ((newNote || "") !== (parent.note || "")) payload.note = newNote;
      if ((newPhoto || null) !== (parent.photo_storage_path || null)) payload.photo_storage_path = newPhoto || "";
      await api.patch(`/admin/parents/${parent.id}`, payload);
      toast.success(`Renamed to ${newName}`);
      setRenaming(null); setRenameConfirm(null); load();
    } catch (err) { toast.error(getErrorMessage(err, "Rename failed")); }
  };

  const openMerge = (p) => { setMerging(p); setMergeTargetId(""); };
  const submitMerge = () => {
    if (!merging || !mergeTargetId || mergeTargetId === merging.id) return;
    const target = items.find((x) => x.id === mergeTargetId);
    const total = (merging.usage_free_text || 0) + (merging.usage_linked || 0);
    const srcNum = extractNumber(merging.name);
    const tgtNum = extractNumber(target?.name || "");
    const crossNumber = srcNum && tgtNum && srcNum !== tgtNum;
    setMergeConfirm({ source: merging, target, affected: total, crossNumber, srcNum, tgtNum });
  };
  const applyMerge = async () => {
    const { source, target, crossNumber } = mergeConfirm;
    try {
      const { data } = await api.post(`/admin/parents/${source.id}/merge`, {
        target_id: target.id,
        confirm_cross_number: !!crossNumber,
      });
      toast.success(`Merged into ${data.merged_into} — ${data.affected_seed + data.affected_pollen} product refs updated`);
      setMerging(null); setMergeConfirm(null); load();
    } catch (err) { toast.error(getErrorMessage(err, "Merge failed")); }
  };

  const extractNumber = (name) => {
    if (!name) return null;
    const m = /['\u2018\u2019]\s*(\d+)\s*['\u2018\u2019]\s*$/.exec(name.trim());
    return m ? m[1] : null;
  };

  const submitDelete = async () => {
    const p = deleting;
    if (!p) return;
    try {
      const total = (p.usage_free_text || 0) + (p.usage_linked || 0);
      const url = total > 0 ? `/admin/parents/${p.id}?force=true` : `/admin/parents/${p.id}`;
      await api.delete(url);
      toast.success("Parent deleted");
      setDeleting(null); load();
    } catch (err) { toast.error(getErrorMessage(err, "Delete failed")); }
  };

  return (
    <>
      <PageHeader
        title="Parents registry"
        subtitle="Canonical mother-plant / cross-parent names. Renames cascade to every product referencing the old name."
        testid="page-parents"
        actions={
          <Button onClick={() => setAdding(true)} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white" data-testid="parents-add-open">
            <Plus size={14} /> Add parent
          </Button>
        }
      />
      <div className="p-4 sm:p-6 max-w-4xl">
        <div className="bg-[#F9F9F8] border border-[#E2E4E0] rounded-md p-2 mb-3">
          <Input data-testid="parents-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by name…" className="h-9" />
        </div>
        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[#F9F9F8] text-[#5C665A]">
                <tr>
                  <th className="w-12"></th>
                  <th className="text-left px-3 py-2 db-h font-semibold">Name</th>
                  <th className="text-left px-3 py-2 db-h font-semibold">Note</th>
                  <th className="text-right px-3 py-2 db-h font-semibold">Used by</th>
                  <th className="text-left px-3 py-2 db-h font-semibold">Product link</th>
                  <th className="px-3 py-2 w-40"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#5C665A]" data-testid="parents-empty">No parents.</td></tr>}
                {filtered.map((p) => {
                  const totalUsage = (p.usage_free_text || 0) + (p.usage_linked || 0);
                  return (
                    <tr key={p.id} className="border-t border-[#E2E4E0]" data-testid={`parents-row-${p.id}`}>
                      <td className="px-2 py-2">
                        {p.photo_storage_path ? (
                          <img src={fileUrl(p.photo_storage_path)} alt="" className="w-10 h-10 object-cover rounded-md border border-[#E2E4E0]" data-testid={`parents-thumb-${p.id}`} />
                        ) : (
                          <div className="w-10 h-10 rounded-md border border-dashed border-[#E2E4E0] bg-[#F9F9F8] grid place-items-center text-[#B7C0B4]">
                            <ImageIcon size={14} />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-medium">{p.name}</td>
                      <td className="px-3 py-2 text-xs text-[#5C665A] max-w-[220px] truncate" title={p.note || ""}>{p.note || "—"}</td>
                      <td className="px-3 py-2 text-right db-mono" data-testid={`parents-usage-${p.id}`}>{totalUsage}</td>
                      <td className="px-3 py-2 text-xs">
                        {p.linked_product_id ? (
                          <Badge className={`border-transparent ${p.linked_product_status === "published" ? "bg-[#E9F5EC] text-[#1B4332]" : "bg-neutral-100 text-neutral-600"}`}>
                            product · {p.linked_product_status}
                          </Badge>
                        ) : (
                          <span className="text-[#5C665A]">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <button onClick={() => openRename(p)} className="text-[#2D6A4F] hover:bg-[#E9F5EC] inline-flex items-center h-7 px-2 rounded-md text-xs mr-1" data-testid={`parents-rename-${p.id}`}>
                          <Pencil size={12} /> Rename
                        </button>
                        <button onClick={() => openMerge(p)} className="text-[#8A5A2B] hover:bg-[#FBF3E8] inline-flex items-center h-7 px-2 rounded-md text-xs mr-1" data-testid={`parents-merge-${p.id}`}>
                          <GitMerge size={12} /> Merge
                        </button>
                        <button onClick={() => setDeleting(p)} className="text-[#C94A4A] hover:bg-[#FDF3F3] inline-flex items-center h-7 w-7 rounded-md justify-center" data-testid={`parents-delete-${p.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add dialog */}
      <AddParentDialog open={adding} onOpenChange={setAdding} onCreated={load} />

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent data-testid="parents-rename-dialog">
          <DialogHeader>
            <DialogTitle>Edit “{renaming?.name}”</DialogTitle>
            <DialogDescription>Name changes cascade to every product free-text reference. Note & photo update in place.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <label className="text-xs text-[#5C665A]">Canonical name</label>
              <Input data-testid="parents-rename-input" value={renameName} onChange={(e) => setRenameName(e.target.value)} className="h-9" />
            </div>
            <div>
              <label className="text-xs text-[#5C665A]">Note (optional)</label>
              <Textarea data-testid="parents-rename-note" value={renameNote} onChange={(e) => setRenameNote(e.target.value)} rows={2} />
            </div>
            <div>
              <label className="text-xs text-[#5C665A]">Photo (optional)</label>
              <ParentPhotoField
                testid="parents-rename-photo"
                value={renamePhoto}
                onChange={setRenamePhoto}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)} data-testid="parents-rename-cancel">Cancel</Button>
            <Button onClick={submitRename} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white" data-testid="parents-rename-continue">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename confirmation with cascade count */}
      <ConfirmDialog
        open={!!renameConfirm}
        onOpenChange={(o) => !o && setRenameConfirm(null)}
        title="Confirm rename cascade"
        description={renameConfirm ? (
          <span>
            Rename “<b>{renameConfirm.parent.name}</b>” → “<b>{renameConfirm.newName}</b>”.
            This will update <b>{renameConfirm.affected}</b> product parent reference{renameConfirm.affected === 1 ? "" : "s"}.
            This cannot be undone from the UI.
          </span>
        ) : ""}
        confirmLabel="Rename with cascade"
        onConfirm={applyRename}
        testid="parents-rename-confirm"
      />

      {/* Merge dialog */}
      <Dialog open={!!merging} onOpenChange={(o) => !o && setMerging(null)}>
        <DialogContent data-testid="parents-merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge “{merging?.name}” into…</DialogTitle>
            <DialogDescription>All products referencing “{merging?.name}” will be updated to point at the target. The source registry entry is deleted.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-xs text-[#5C665A]">Target parent</label>
            <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
              <SelectTrigger className="h-9" data-testid="parents-merge-target"><SelectValue placeholder="Choose target…" /></SelectTrigger>
              <SelectContent>
                {items.filter((x) => x.id !== merging?.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMerging(null)} data-testid="parents-merge-cancel">Cancel</Button>
            <Button onClick={submitMerge} disabled={!mergeTargetId} className="bg-[#8A5A2B] hover:bg-[#6f4820] text-white disabled:opacity-50" data-testid="parents-merge-continue">
              <GitMerge size={14} /> Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!mergeConfirm}
        onOpenChange={(o) => !o && setMergeConfirm(null)}
        title="Confirm merge"
        description={mergeConfirm ? (
          <span>
            {mergeConfirm.crossNumber && (
              <span className="block mb-2 p-2 rounded-md bg-[#FDF3F3] border border-[#f2c7c7] text-[#C94A4A] text-xs" data-testid="parents-merge-cross-warn">
                ⚠ Cross-number merge: source is <b>'{mergeConfirm.srcNum}'</b> but target is <b>'{mergeConfirm.tgtNum}'</b>.
                Numbers uniquely identify mother plants — this is usually a mistake.
              </span>
            )}
            Merge “<b>{mergeConfirm.source.name}</b>” into “<b>{mergeConfirm.target.name}</b>”.
            Affects <b>{mergeConfirm.affected}</b> product parent reference{mergeConfirm.affected === 1 ? "" : "s"} and
            deletes the “{mergeConfirm.source.name}” registry row.
          </span>
        ) : ""}
        confirmLabel={mergeConfirm?.crossNumber ? "Merge anyway (I know)" : "Merge and delete source"}
        destructive
        onConfirm={applyMerge}
        testid="parents-merge-confirm"
      />

      {/* Delete confirmation (offers force when in-use) */}
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={deleting ? `Delete “${deleting.name}”?` : ""}
        description={deleting ? (
          (deleting.usage_free_text || 0) + (deleting.usage_linked || 0) > 0 ? (
            <span>
              <Info size={14} className="inline mr-1 text-[#8A5A2B]" />
              This parent is used by <b>{(deleting.usage_free_text || 0) + (deleting.usage_linked || 0)}</b> product{(deleting.usage_free_text || 0) + (deleting.usage_linked || 0) === 1 ? "" : "s"}.
              Deleting force-drops the registry row; product references keep their free-text name.
              Merging or renaming is usually safer.
            </span>
          ) : "This will remove the parent from the registry. No products reference it."
        ) : ""}
        confirmLabel="Delete parent"
        destructive
        onConfirm={submitDelete}
        testid="parents-delete-confirm"
      />
    </>
  );
}

function AddParentDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/parents", {
        name: name.trim(), note: note.trim(),
        photo_storage_path: photo || null,
      });
      toast.success("Parent added");
      setName(""); setNote(""); setPhoto(null); onOpenChange(false); onCreated();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="parents-add-dialog">
        <DialogHeader>
          <DialogTitle>Add parent</DialogTitle>
          <DialogDescription>Adds a canonical mother-plant name usable in product lineage autocompletion.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div>
            <label className="text-xs text-[#5C665A]">Canonical name</label>
            <Input data-testid="parents-add-name" value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Note (optional)</label>
            <Textarea data-testid="parents-add-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                      placeholder="Provenance, breeder, retired mother, …" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Photo (optional)</label>
            <ParentPhotoField testid="parents-add-photo" value={photo} onChange={setPhoto} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="parents-add-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white" data-testid="parents-add-submit">
            {busy ? "Adding…" : "Add parent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParentPhotoField({ testid, value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.storage_path);
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed"));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-16 h-16 rounded-md border border-[#E2E4E0] bg-[#F9F9F8] overflow-hidden grid place-items-center text-[#B7C0B4] shrink-0" data-testid={`${testid}-preview`}>
        {value ? <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={20} />}
      </div>
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-input`} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
                className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-[#E2E4E0] hover:bg-[#F9F9F8] text-xs" data-testid={`${testid}-upload`}>
          <Upload size={12} /> {busy ? "Uploading…" : (value ? "Replace" : "Upload")}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")}
                  className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-[#f2c7c7] text-[#C94A4A] hover:bg-[#FDF3F3] text-xs" data-testid={`${testid}-clear`}>
            <X size={12} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

// silence unused-imports the linter can't infer through JSX
void X;
